import {
  DiseaseInfo,
  CmlPhaseRow,
  RiskGroup,
  RiskLevel,
  ResponseMilestone,
  ResponseClassification,
  MrDepthRow,
  DiagnosticCriteriaSet,
} from '../types';

// ══════════════════════════════════════════════════════
// Disease definitions
// ══════════════════════════════════════════════════════

export const diseases: DiseaseInfo[] = [
  {
    id: 'cml',
    nameHebrew: 'לוקמיה מיאלואידית כרונית',
    nameEnglish: 'Chronic Myeloid Leukemia',
    abbreviation: 'CML',
    summaryKey: 'diseases.cml.summary',
    tabs: [
      { id: 'diagnosis', labelKey: 'diseases.cml.tabs.diagnosis' },
      { id: 'elts', labelKey: 'diseases.cml.tabs.elts' },
      { id: 'response', labelKey: 'diseases.cml.tabs.response' },
    ],
    references: [
      '2025 European LeukemiaNet recommendations for the management of chronic myeloid leukemia',
      'NCCN 2026',
      '2025 Treatment-Free Remission in Chronic Myeloid Leukemia: Revisiting the "W" Questions',
    ],
  },
  {
    id: 'pv',
    nameHebrew: 'פוליציטמיה ורה',
    nameEnglish: 'Polycythemia Vera',
    abbreviation: 'PV',
    summaryKey: 'diseases.pv.summary',
    tabs: [
      { id: 'diagnosis', labelKey: 'diseases.pv.tabs.diagnosis' },
      { id: 'risk', labelKey: 'diseases.pv.tabs.risk' },
    ],
    references: [
      'Polycythemia vera — Martin H. Ellis, MD; Tiziano Barbui, MD; Ayalew Tefferi, MD',
      'Diagnosis and Treatment of Polycythemia Vera: A Review — Tremblay D, Kremyanskaya M, Mascarenhas J, Hoffman R',
      'NCCN Myeloproliferative Neoplasms version 2.2026',
    ],
  },
  {
    id: 'et',
    nameHebrew: 'תרומבוציטוזיס חיונית',
    nameEnglish: 'Essential Thrombocytosis',
    abbreviation: 'ET',
    summaryKey: 'diseases.et.summary',
    tabs: [
      { id: 'diagnosis', labelKey: 'diseases.et.tabs.diagnosis' },
      { id: 'risk', labelKey: 'diseases.et.tabs.risk' },
    ],
    references: [
      'Essential thrombocythemia: 2024 update on diagnosis, risk stratification, and management — Tefferi A, Vannucchi AM, Barbui T',
      'NCCN Myeloproliferative Neoplasms version 2.2026',
    ],
  },
  {
    id: 'mf',
    nameHebrew: 'מיאלופיברוזיס',
    nameEnglish: 'Myelofibrosis',
    abbreviation: 'MF',
    summaryKey: 'diseases.mf.summary',
    tabs: [
      { id: 'diagnosis', labelKey: 'diseases.mf.tabs.diagnosis' },
      { id: 'risk', labelKey: 'diseases.mf.tabs.risk' },
      { id: 'mpn10', labelKey: 'diseases.mf.tabs.mpn10' },
    ],
    references: [
      'Primary myelofibrosis: 2023 update on diagnosis, risk-stratification and management — Tefferi A',
      'NCCN Myeloproliferative Neoplasms version 2.2026',
      'MIPSS70+ Version 2.0 — Tefferi A, Guglielmelli P, Lasho TL, et al',
    ],
  },
  {
    id: 'cmml',
    nameHebrew: 'לוקמיה מיאלומונוציטית כרונית',
    nameEnglish: 'Chronic Myelomonocytic Leukemia',
    abbreviation: 'CMML',
    summaryKey: 'diseases.cmml.summary',
    tabs: [
      { id: 'diagnosis', labelKey: 'diseases.cmml.tabs.diagnosis' },
      { id: 'risk', labelKey: 'diseases.cmml.tabs.risk' },
    ],
    references: [
      'Chronic myelomonocytic leukemia: 2024 update on diagnosis, risk stratification and management — Patnaik MM, Tefferi A',
      'NCCN Myelodysplastic Syndromes version 1.2026',
      'Risk Stratification for Overall Survival in 457 CMML Patients Using the BLAST Model — Blood 2025',
    ],
  },
];

// ══════════════════════════════════════════════════════
// CML Data
// ══════════════════════════════════════════════════════

export const cmlPhaseData: CmlPhaseRow[] = [
  {
    source: 'WHO 2022',
    chronicPhase:
      'Blasts (PB & BM) < 20%\n\nHigh risk indicators\nAt diagnosis:\n• High ELTS score\n• Blasts (PB & BM) 10–19%\n• PB basophils ≥20%\n• ACA: 3q26.2 rearrangements, -7, i(17q) & complex karyotype\n• Clusters of small megakaryocytes with fibrosis\n\nOn treatment:\n• No CHR on 1st line TKI\n• Resistance to 2GTKI (unless due to a BCR::ABL1 mutation)\n• Development of ACA\n• Compound mutations in BCR::ABL1',
    acceleratedPhase: 'No longer exists',
    blastPhase:
      'Blasts (PB or BM) ≥ 20%\nExtramedullary blast proliferation\nBona fide lymphoblasts in PB or BM (even if <10%)',
  },
  {
    source: 'ELN',
    chronicPhase: 'Blasts (PB & BM) < 15%',
    acceleratedPhase:
      'Blasts (PB or BM) 15-29%\nPB basophils ≥20%\nPlatelets <100 × 10⁹/L unrelated to treatment\nMajor route ACA emerging on treatment',
    blastPhase:
      'Blasts (PB or BM) ≥ 30%\nExtramedullary blast proliferation',
  },
];

/** https://www.leukemia-net.org/leukemias/cml/elts_score/ */
export const ELTS_CALCULATOR_URL =
  'https://www.leukemia-net.org/leukemias/cml/elts_score/';

export const eltsRiskGroups: RiskGroup[] = [
  {
    level: 'low',
    label: 'Low risk',
    tone: 'green',
    criteria: 'ELTS ≤ 1.5680',
  },
  {
    level: 'intermediate',
    label: 'Intermediate risk',
    tone: 'yellow',
    criteria: '1.5680 < ELTS ≤ 2.2185',
  },
  {
    level: 'high',
    label: 'High risk',
    tone: 'red',
    criteria: 'ELTS > 2.2185',
  },
];

export const responseMilestones: ResponseMilestone[] = [
  {
    timePoint: '3 months',
    favorable: '≤10%',
    warning: '>10%',
    unfavorable: '>10% if confirmed within 1-3 months',
  },
  {
    timePoint: '6 months',
    favorable: '≤1%',
    warning: '>1–10%',
    unfavorable: '>10% established resistance',
  },
  {
    timePoint: '12 months',
    favorable: '≤0.1%',
    warning: '>0.1-1%',
    unfavorable: '>1%',
  },
  {
    timePoint: 'At any time',
    favorable: '≤0.1%',
    warning: '>0.1–1% loss of ≤0.1% (MMR)',
    unfavorable:
      'Loss of a previous response, resistant BCR::ABL1 mutations, high-risk ACA',
  },
];

/**
 * Classify a BCR::ABL1 IS value at a given milestone, per ELN Table 4.
 *
 * The table is not a plain three-band split: at 3 months anything >10% is a
 * *warning*, and only becomes unfavorable once a repeat test 1–3 months later
 * confirms it. Cases like that are carried in `note` rather than silently
 * upgraded to a worse category.
 */
export const classifyResponse = (
  timePoint: string,
  bcrAbl: number
): ResponseClassification | null => {
  switch (timePoint) {
    case '3 months':
      if (bcrAbl <= 10) return { category: 'favorable' };
      return {
        category: 'warning',
        noteKey: 'response.notes.3monthsWarning',
      };

    case '6 months':
      if (bcrAbl <= 1) return { category: 'favorable' };
      if (bcrAbl <= 10) return { category: 'warning' };
      return { category: 'unfavorable', noteKey: 'response.notes.6monthsUnfavorable' };

    case '12 months':
      if (bcrAbl <= 0.1) return { category: 'favorable' };
      if (bcrAbl <= 1) return { category: 'warning' };
      if (bcrAbl <= 10) {
        return {
          category: 'unfavorable',
          noteKey: 'response.notes.12monthsUnfavorable',
        };
      }
      return { category: 'unfavorable' };

    case 'At any time':
      if (bcrAbl <= 0.1) return { category: 'favorable' };
      if (bcrAbl <= 1) {
        return {
          category: 'warning',
          noteKey: 'response.notes.anyTimeWarning',
        };
      }
      return { category: 'unfavorable' };

    default:
      return null;
  }
};

export const mrDepthData: MrDepthRow[] = [
  {
    terminology: 'Equivalent to complete cytogenetic remission (CCyR)',
    abbreviation: 'CCyR',
    bcrAblThreshold: '≤1%',
    thresholdValue: 1,
    minAbl1Transcripts: '10,000',
  },
  {
    terminology: 'Major molecular response (MMR)',
    abbreviation: 'MMR (MR3)',
    bcrAblThreshold: '≤0.1%',
    thresholdValue: 0.1,
    minAbl1Transcripts: '10,000',
  },
  {
    terminology: '4 log reduction from the IRIS standardized baseline (MR⁴)',
    abbreviation: 'MR4',
    bcrAblThreshold: '≤0.01%',
    thresholdValue: 0.01,
    minAbl1Transcripts: '10,000',
  },
  {
    terminology:
      '4.5 log reduction from the IRIS standardized baseline (MR⁴·⁵)',
    abbreviation: 'MR4.5',
    bcrAblThreshold: '≤0.0032%',
    thresholdValue: 0.0032,
    minAbl1Transcripts: '32,000',
  },
  {
    terminology: '5 log reduction from the IRIS standardized baseline (MR⁵)',
    abbreviation: 'MR5',
    bcrAblThreshold: '≤0.001%',
    thresholdValue: 0.001,
    minAbl1Transcripts: '100,000',
  },
];

/** A value at or below this threshold is a Major Molecular Response. */
export const MMR_THRESHOLD = 0.1;

// ══════════════════════════════════════════════════════
// PV Data
// ══════════════════════════════════════════════════════

export const pvDiagnosticCriteria: DiagnosticCriteriaSet = {
  majorCriteria: [
    {
      number: 1,
      text: 'Hb >16.5 g/dL in men or >16.0 g/dL in women\nor\nHematocrit >49% in men, >48% in women\nor\nRed blood cell mass >25% above mean predicted value',
      notes: 'Red blood cell mass is not a criterion for WHO diagnosis of PV',
    },
    {
      number: 2,
      text: 'Bone marrow biopsy showing age-adjusted hypercellularity with trilineage proliferation (panmyelosis), including prominent erythroid, granulocytic, and increase in pleomorphic, mature megakaryocytes without atypia',
      notes:
        'Bone marrow biopsy may not be required in patients with sustained absolute erythrocytosis [Hb >18.5 g/dL in men or >16.6 g/dL in women and hematocrit >55.5% in men or >49.5% in women] and the presence of a JAK2 V617F or JAK2 exon 12 gene variant',
    },
    {
      number: 3,
      text: 'Presence of JAK2 V617F gene variant\nor\nPresence of JAK2 exon 12 gene variant',
    },
  ],
  minorCriteria: [
    {
      number: 1,
      text: 'Subnormal serum erythropoietin level',
    },
  ],
  diagnosisRule:
    'All 3 major criteria, OR the first 2 major + the minor criterion',
};

export const pvRiskGroups: RiskGroup[] = [
  {
    level: 'low',
    label: 'Low-risk (PV-1)',
    tone: 'green',
    criteria: 'Age <60 years and no prior history of thrombosis',
  },
  {
    level: 'high',
    label: 'High-risk (PV-2)',
    tone: 'red',
    criteria: 'Age ≥60 years and/or prior history of thrombosis',
  },
];

// ══════════════════════════════════════════════════════
// ET Data
// ══════════════════════════════════════════════════════

export const etDiagnosticCriteria: DiagnosticCriteriaSet = {
  majorCriteria: [
    {
      number: 1,
      text: 'Platelet count ≥450 × 10⁹/L',
    },
    {
      number: 2,
      text: 'BM megakaryocyte proliferation with mature cytology, hyperlobulated nuclei, and in loose clusters, BM fibrosis absent or ≤grade 1',
    },
    {
      number: 3,
      text: 'Not meeting ICC criteria for other myeloid neoplasms, including PV, prefibrotic MF, and CML',
    },
    {
      number: 4,
      text: 'JAK2, CALR or MPL mutation',
    },
  ],
  minorCriteria: [
    {
      number: 1,
      text: 'Other clonal marker present',
    },
    {
      number: 2,
      text: 'No evidence of reactive thrombocytosis',
    },
  ],
  diagnosisRule:
    'All 4 major criteria, OR the first 3 major + 1 minor criterion',
};

export const etRiskGroups: RiskGroup[] = [
  {
    level: 'very-low',
    label: 'Very-low-risk',
    tone: 'green',
    criteria: 'Age ≤60 years, no JAK2 mutation, no prior history of thrombosis',
  },
  {
    level: 'low',
    label: 'Low-risk',
    tone: 'green',
    criteria: 'Age ≤60 years, with JAK2 mutation, no prior history of thrombosis',
  },
  {
    level: 'intermediate',
    label: 'Intermediate-risk',
    tone: 'yellow',
    criteria: 'Age >60 years, no JAK2 mutation, no prior history of thrombosis',
  },
  {
    level: 'high',
    label: 'High-risk',
    tone: 'red',
    criteria:
      'History of thrombosis at any age, or age >60 years with JAK2 mutation',
  },
];

// ══════════════════════════════════════════════════════
// MF Data
// ══════════════════════════════════════════════════════

// ── MF Diagnostic Criteria ──

export const mfOvertDiagnosticCriteria: DiagnosticCriteriaSet = {
  majorCriteria: [
    {
      number: 1,
      text: 'Megakaryocyte proliferation and atypia, accompanied by ≥grade 2 reticulin/collagen fibrosis',
      notes: 'Atypia: aberrant nuclear/cytoplasmic ratio; hyperchromatic, irregularly folded nuclei; dense megakaryocyte clustering. Fibrosis: diffuse, often coarse fiber network with or without collagenization (trichrome stain)',
    },
    {
      number: 2,
      text: 'Presence of JAK2, CALR or MPL mutations, or presence of other clonal markers, or absence of evidence for reactive bone marrow fibrosis',
    },
    {
      number: 3,
      text: 'Not meeting ICC criteria for other myeloid neoplasms',
    },
  ],
  minorCriteria: [
    { number: 1, text: 'Anemia not otherwise explained' },
    { number: 2, text: 'Leukocytosis ≥11 × 10⁹/L' },
    { number: 3, text: 'Palpable splenomegaly' },
    { number: 4, text: 'Increased serum lactate dehydrogenase' },
    { number: 5, text: 'A leukoerythroblastic blood smear' },
  ],
  diagnosisRule: 'All 3 major criteria + at least 1 minor criterion',
};

export const mfPrefibDiagnosticCriteria: DiagnosticCriteriaSet = {
  majorCriteria: [
    {
      number: 1,
      text: 'Megakaryocyte proliferation and atypia, accompanied by ≤grade 1 reticulin/collagen fibrosis, granulocyte proliferation/decreased erythropoiesis',
      notes: 'Atypia: aberrant nuclear/cytoplasmic ratio; hyperchromatic, irregularly folded nuclei; dense megakaryocyte clustering',
    },
    {
      number: 2,
      text: 'Presence of JAK2, CALR or MPL mutations, or presence of other clonal markers, or absence of evidence for reactive bone marrow fibrosis',
    },
    {
      number: 3,
      text: 'Not meeting ICC criteria for other myeloid neoplasms',
    },
  ],
  minorCriteria: [
    { number: 1, text: 'Anemia not otherwise explained' },
    { number: 2, text: 'Leukocytosis ≥11 × 10⁹/L' },
    { number: 3, text: 'Palpable splenomegaly' },
    { number: 4, text: 'Increased serum lactate dehydrogenase' },
  ],
  diagnosisRule: 'All 3 major criteria + at least 1 minor criterion',
};

export const mfPostPvDiagnosticCriteria: DiagnosticCriteriaSet = {
  majorCriteria: [
    { number: 1, text: 'Documentation of a previous diagnosis of PV (as defined by ICC/WHO criteria)' },
    { number: 2, text: 'Bone marrow fibrosis grade 2–3 (on 0–3 scale) or grade 3–4 (on 0–4 scale)' },
  ],
  minorCriteria: [
    { number: 1, text: 'New-onset anemia (sustained decrease in Hb below the reference range) OR sustained loss of phlebotomy requirement' },
    { number: 2, text: 'Leukoerythroblastosis' },
    { number: 3, text: 'Increasing splenomegaly (≥5 cm increase from baseline OR new palpable splenomegaly)' },
    { number: 4, text: 'Development of ≥1 of 3 constitutional symptoms: >10% weight loss over 6 months, night sweats, unexplained fever (>37.5°C)' },
  ],
  diagnosisRule: 'Both required criteria + at least 2 additional criteria',
};

export const mfPostEtDiagnosticCriteria: DiagnosticCriteriaSet = {
  majorCriteria: [
    { number: 1, text: 'Documentation of a previous diagnosis of ET (as defined by ICC/WHO criteria)' },
    { number: 2, text: 'Bone marrow fibrosis grade 2–3 (on 0–3 scale) or grade 3–4 (on 0–4 scale)' },
  ],
  minorCriteria: [
    { number: 1, text: 'Anemia and ≥2 g/dL decrease in hemoglobin level (from baseline)' },
    { number: 2, text: 'Leukoerythroblastosis' },
    { number: 3, text: 'Increasing splenomegaly (≥5 cm increase from baseline OR new palpable splenomegaly)' },
    { number: 4, text: 'Elevated LDH above upper normal limit' },
    { number: 5, text: 'Development of ≥1 of 3 constitutional symptoms: >10% weight loss over 6 months, night sweats, unexplained fever (>37.5°C)' },
  ],
  diagnosisRule: 'Both required criteria + at least 2 additional criteria',
};

// ── MF diagnosis evaluation ──
// Both receive the 1-based numbers of the criteria marked as met.

export interface DiagnosisVerdict {
  met: boolean;
  label: string;
}

/** Primary MF (overt / pre-fibrotic, ICC): all 3 major + at least 1 minor */
export const evaluatePrimaryMf = (majorMet: number[], minorMet: number[]): DiagnosisVerdict => {
  const allMajor = [1, 2, 3].every((n) => majorMet.includes(n));
  if (allMajor && minorMet.length >= 1) {
    return { met: true, label: 'Diagnosis met — all 3 Major + ≥1 Minor' };
  }
  return { met: false, label: 'Diagnosis not met' };
};

/** Post-PV / post-ET MF (IWG-MRT): both required + at least 2 additional */
export const evaluatePostMf = (majorMet: number[], minorMet: number[]): DiagnosisVerdict => {
  const bothRequired = [1, 2].every((n) => majorMet.includes(n));
  if (bothRequired && minorMet.length >= 2) {
    return { met: true, label: 'Diagnosis met — both required + ≥2 additional' };
  }
  return { met: false, label: 'Diagnosis not met' };
};

// ── DIPSS ──

export const dipssRiskGroups: RiskGroup[] = [
  { level: 'low', label: 'Low', tone: 'green', criteria: '0 points' },
  { level: 'int-1', label: 'Intermediate-1', tone: 'green', criteria: '1–2 points' },
  { level: 'int-2', label: 'Intermediate-2', tone: 'yellow', criteria: '3–4 points' },
  { level: 'high', label: 'High', tone: 'red', criteria: '5–6 points' },
];

/**
 * Calculate DIPSS score.
 * Variables (Hb counts for 2 pts):
 *  Age >65 → 1 pt
 *  WBC >25×10⁹/L → 1 pt
 *  Hb <10 g/dL → 2 pts
 *  Peripheral blood blasts ≥1% → 1 pt
 *  Constitutional symptoms → 1 pt
 */
export const calculateDipss = (
  age: number,
  wbc: number,
  hb: number,
  blasts: number,
  symptoms: boolean
): { score: number; level: RiskLevel } => {
  let score = 0;
  if (age > 65) score += 1;
  if (wbc > 25) score += 1;
  if (hb < 10) score += 2;
  if (blasts >= 1) score += 1;
  if (symptoms) score += 1;

  let level: RiskLevel;
  if (score === 0) level = 'low';
  else if (score <= 2) level = 'int-1';
  else if (score <= 4) level = 'int-2';
  else level = 'high';

  return { score, level };
};

// ── DIPSS-Plus ──

export const dipssPlusRiskGroups: RiskGroup[] = [
  { level: 'low', label: 'Low', tone: 'green', criteria: '0 points' },
  { level: 'int-1', label: 'Intermediate-1', tone: 'green', criteria: '1 point' },
  { level: 'int-2', label: 'Intermediate-2', tone: 'yellow', criteria: '2–3 points' },
  { level: 'high', label: 'High', tone: 'red', criteria: '≥4 points' },
];

/**
 * Calculate DIPSS-Plus score.
 * DIPSS risk category → Low=0, Int-1=1, Int-2=2, High=3
 * + Platelets <100×10⁹/L → 1 pt
 * + Transfusion need → 1 pt
 * + Unfavorable karyotype → 1 pt
 */
export const calculateDipssPlus = (
  dipssLevel: RiskLevel,
  plateletsLow: boolean,
  transfusionNeed: boolean,
  unfavorableKaryotype: boolean
): { score: number; level: RiskLevel } => {
  const dipssMap: Record<string, number> = { low: 0, 'int-1': 1, 'int-2': 2, high: 3 };
  let score = dipssMap[dipssLevel] ?? 0;
  if (plateletsLow) score += 1;
  if (transfusionNeed) score += 1;
  if (unfavorableKaryotype) score += 1;

  let level: RiskLevel;
  if (score === 0) level = 'low';
  else if (score === 1) level = 'int-1';
  else if (score <= 3) level = 'int-2';
  else level = 'high';

  return { score, level };
};

// ── MIPSS-70+ v2.0 ──

export const mipss70RiskGroups: RiskGroup[] = [
  { level: 'very-low', label: 'Very Low', tone: 'green', criteria: '0 points' },
  { level: 'low', label: 'Low', tone: 'green', criteria: '1–2 points' },
  { level: 'intermediate', label: 'Intermediate', tone: 'yellow', criteria: '3–4 points' },
  { level: 'high', label: 'High', tone: 'red', criteria: '5–8 points' },
  { level: 'very-high', label: 'Very High', tone: 'red', criteria: '≥9 points' },
];

/**
 * Calculate MIPSS-70+ v2.0 score.
 *
 * Severe anemia (sex-dependent): Hb <8 (F) or <9 (M) → 2 pts
 * Moderate anemia: Hb 8–<10 (F) or 9–<11 (M) → 1 pt
 * Circulating blasts ≥2% → 1 pt
 * Constitutional symptoms → 2 pts
 * Absence of CALR type 1 mutation → 2 pts
 * HMR mutation count: 1 HMR → 2 pts, ≥2 HMR → 3 pts (mutually exclusive)
 * Karyotype: unfavorable → 3 pts, VHR → 4 pts (mutually exclusive)
 */
export const calculateMipss70 = (
  sex: 'male' | 'female',
  hb: number,
  blasts: number,
  symptoms: boolean,
  calrType1: boolean,
  hmrCount: number, // 0, 1, or 2+
  karyotype: 'favorable' | 'unfavorable' | 'vhr'
): { score: number; level: RiskLevel } => {
  let score = 0;

  // Anemia — sex-dependent thresholds
  if (sex === 'female') {
    if (hb < 8) score += 2;
    else if (hb < 10) score += 1;
  } else {
    if (hb < 9) score += 2;
    else if (hb < 11) score += 1;
  }

  if (blasts >= 2) score += 1;
  if (symptoms) score += 2;
  if (!calrType1) score += 2;

  // HMR mutations — pick one tier
  if (hmrCount >= 2) score += 3;
  else if (hmrCount === 1) score += 2;

  // Karyotype — pick one tier
  if (karyotype === 'vhr') score += 4;
  else if (karyotype === 'unfavorable') score += 3;

  let level: RiskLevel;
  if (score === 0) level = 'very-low';
  else if (score <= 2) level = 'low';
  else if (score <= 4) level = 'intermediate';
  else if (score <= 8) level = 'high';
  else level = 'very-high';

  return { score, level };
};

// ── MPN-10 Symptom Names ──

export const MPN10_SYMPTOMS = [
  { en: 'Fatigue', he: 'עייפות' },
  { en: 'Early satiety', he: 'שובע מוקדם' },
  { en: 'Abdominal discomfort', he: 'אי-נוחות בבטן' },
  { en: 'Inactivity', he: 'חוסר פעילות' },
  { en: 'Concentration problems', he: 'בעיות ריכוז' },
  { en: 'Night sweats', he: 'הזעות לילה' },
  { en: 'Itching (pruritus)', he: 'גרד' },
  { en: 'Bone pain', he: 'כאבי עצמות' },
  { en: 'Fever (>37.8°C)', he: 'חום (מעל 37.8°C)', hint: '0 (absent) to 10 (daily)' },
  { en: 'Weight loss', he: 'ירידה במשקל' },
];

// ══════════════════════════════════════════════════════
// CMML Data
// ══════════════════════════════════════════════════════

// ── CMML Diagnostic Criteria ──

// WHO 2022: the four prerequisites are the "major" list and the three supporting
// criteria the "minor" list. How many supporting criteria are needed depends on the
// monocyte count, which is asked separately (see evaluateCmmlWho2022).
export const cmmlWho2022Criteria: DiagnosticCriteriaSet = {
  majorCriteria: [
    {
      number: 1,
      text: 'Persistent absolute ≥0.5 × 10⁹/L and relative ≥10% peripheral blood monocytosis',
    },
    {
      number: 2,
      text: 'Blasts constituting <20% of cells in the bone marrow and peripheral blood',
      notes: 'Blasts and blast equivalents include myeloblasts, monoblasts and promonocytes',
    },
    {
      number: 3,
      text: 'Not meeting the criteria for CML or any other MPNs',
      notes: 'A documented history of MPN excludes CMML. MPN features in the bone marrow and/or a high burden of JAK2, CALR or MPL mutations favour MPN with monocytosis',
    },
    {
      number: 4,
      text: 'Not meeting the criteria of myeloid/lymphoid neoplasms with tyrosine kinase fusions',
      notes: 'PDGFRA, PDGFRB, FGFR1, JAK2, FLT3, ETV6::ABL1 — specifically excluded in cases with eosinophilia',
    },
  ],
  minorCriteria: [
    {
      number: 1,
      text: 'Dysplasia of at least 1 myeloid lineage',
      notes: 'Morphologic dysplasia in ≥10% of cells of a haematopoietic lineage in the bone marrow',
    },
    {
      number: 2,
      text: 'Acquired clonal cytogenetic or molecular abnormality',
    },
    {
      number: 3,
      text: 'Abnormal partitioning of peripheral blood monocyte subsets',
      notes: 'Increased classical monocytes (>94%) in the absence of known active autoimmune diseases and/or systemic inflammatory syndromes',
    },
  ],
  diagnosisRule: 'All 4 prerequisite criteria. If monocytosis ≥1 × 10⁹/L: one or more supporting criteria. If monocytosis ≥0.5 and <1 × 10⁹/L: supporting criteria 1 and 2 must both be met',
};

// ICC 2022: six required criteria, except that clonality (#4) can be replaced by
// the two "no clonality" criteria, which are the "minor" list here.
export const cmmlIcc2022Criteria: DiagnosticCriteriaSet = {
  majorCriteria: [
    {
      number: 1,
      text: 'Monocytes ≥0.5 × 10⁹/L and ≥10% of the WBC',
    },
    {
      number: 2,
      text: 'Cytopenia (same thresholds as MDS)',
      notes: 'A small proportion of cases may show only borderline or no cytopenia, usually in early-phase disease',
    },
    {
      number: 3,
      text: 'Blasts (including promonocytes) <20% of cells in blood and bone marrow',
    },
    {
      number: 4,
      text: 'Presence of clonality: abnormal cytogenetics and/or at least one myeloid neoplasm-associated mutation of at least 10% allele frequency',
      notes: 'If absent, both "without clonality" criteria below must be met instead',
    },
    {
      number: 5,
      text: 'Bone marrow examination with morphologic findings consistent with CMML (hypercellularity due to a myeloid proliferation, often with increased monocytes) and lacking diagnostic features of AML, MPN, or other conditions associated with monocytosis',
      notes: 'Without bone marrow findings of CMML, consider CMUS (or CCMUS if cytopenia is present)',
    },
    {
      number: 6,
      text: 'No BCR::ABL1 or genetic abnormalities of myeloid/lymphoid neoplasms with eosinophilia and tyrosine kinase gene fusions',
    },
  ],
  minorCriteria: [
    {
      number: 1,
      text: 'Monocytes ≥1 × 10⁹/L and >10% of the WBC',
    },
    {
      number: 2,
      text: 'Increased blasts (including promonocytes), morphologic dysplasia, or abnormal immunophenotype consistent with CMML',
      notes: 'Increased blasts: ≥5% in the bone marrow and/or ≥2% in the peripheral blood',
    },
  ],
  diagnosisRule: 'Criteria 1, 2, 3, 5 and 6, plus clonality (criterion 4). Without clonality, both "without clonality" criteria must be met instead',
};

/**
 * WHO 2022 CMML. `amcAtLeast1` is whether monocytes are ≥1 × 10⁹/L; it decides how
 * many supporting criteria are needed, so without it the verdict is withheld.
 */
export const evaluateCmmlWho2022 = (
  majorMet: number[],
  minorMet: number[],
  amcAtLeast1: boolean | null
): DiagnosisVerdict => {
  const allPrerequisite = [1, 2, 3, 4].every((n) => majorMet.includes(n));
  if (!allPrerequisite) return { met: false, label: 'Criteria not met — all 4 prerequisite criteria are required' };
  if (amcAtLeast1 === null) {
    return { met: false, label: 'Answer whether monocytes are ≥1 × 10⁹/L to complete the evaluation' };
  }
  if (amcAtLeast1) {
    return minorMet.length >= 1
      ? { met: true, label: 'Criteria met — all prerequisite + ≥1 supporting criterion' }
      : { met: false, label: 'Criteria not met — at least 1 supporting criterion is required' };
  }
  return [1, 2].every((n) => minorMet.includes(n))
    ? { met: true, label: 'Criteria met — all prerequisite + supporting criteria 1 and 2' }
    : { met: false, label: 'Criteria not met — monocytes <1 × 10⁹/L require supporting criteria 1 and 2' };
};

/** ICC 2022 CMML: criteria 1, 2, 3, 5, 6 and either clonality (#4) or both no-clonality criteria */
export const evaluateCmmlIcc2022 = (majorMet: number[], minorMet: number[]): DiagnosisVerdict => {
  const required = [1, 2, 3, 5, 6].every((n) => majorMet.includes(n));
  const clonal = majorMet.includes(4);
  const withoutClonality = [1, 2].every((n) => minorMet.includes(n));
  if (required && clonal) {
    return { met: true, label: 'Criteria met — all 6 criteria fulfilled' };
  }
  if (required && withoutClonality) {
    return { met: true, label: 'Criteria met — without clonality, via the alternative criteria' };
  }
  return { met: false, label: 'Criteria not met' };
};

// ── GFM (2013) ──

export const gfmRiskGroups: RiskGroup[] = [
  { level: 'low', label: 'Low', tone: 'green', criteria: '0–4 points (median survival: N/R)' },
  { level: 'int-1', label: 'Intermediate-1', tone: 'yellow', criteria: '5–7 points (median survival: 38.5 mo)' },
  { level: 'int-2', label: 'Intermediate-2', tone: 'red', criteria: '8–12 points (median survival: 14.4 mo)' },
];

/**
 * GFM score (2013):
 *  Age >65 → 2 pts
 *  WBC >15,000/µL → 3 pts
 *  Anemia: Hb <10 g/dL (F) or <11 g/dL (M) → 2 pts
 *  Platelets <100,000 → 2 pts
 *  ASXL1 mutation → 2 pts
 */
export const calculateGfm = (
  age: number,
  wbc: number,
  hb: number,
  sex: 'male' | 'female',
  plateletsLow: boolean,
  asxl1: boolean
): { score: number; level: RiskLevel } => {
  let score = 0;
  if (age > 65) score += 2;
  if (wbc > 15) score += 3;
  if (sex === 'female' ? hb < 10 : hb < 11) score += 2;
  if (plateletsLow) score += 2;
  if (asxl1) score += 2;

  let level: RiskLevel;
  if (score <= 4) level = 'low';
  else if (score <= 7) level = 'int-1';
  else level = 'int-2';

  return { score, level };
};

// ── Mayo Molecular Model (2014) ──

export const mayoRiskGroups: RiskGroup[] = [
  { level: 'low', label: 'Low', tone: 'green', criteria: '0 points (median survival: 97 mo)' },
  { level: 'int-1', label: 'Intermediate-1', tone: 'green', criteria: '≤2 points (median survival: 59 mo)' },
  { level: 'int-2', label: 'Intermediate-2', tone: 'yellow', criteria: '2.5–4.5 points (median survival: 31 mo)' },
  { level: 'high', label: 'High', tone: 'red', criteria: '≥5 points (median survival: 16 mo)' },
];

/**
 * Mayo Molecular Model (2014):
 *  Hb <10 g/dL → 2 pts
 *  Platelets <100,000 → 2 pts
 *  AMC >10 × 10⁹/L → 2 pts
 *  PB IMCs >0% → 2 pts
 *  ASXL1 frameshift/nonsense mutation → 1.5 pts
 */
export const calculateMayo = (
  hb: number,
  plateletsLow: boolean,
  amc: number,
  imcsPresent: boolean,
  asxl1Frameshift: boolean
): { score: number; level: RiskLevel } => {
  let score = 0;
  if (hb < 10) score += 2;
  if (plateletsLow) score += 2;
  if (amc > 10) score += 2;
  if (imcsPresent) score += 2;
  if (asxl1Frameshift) score += 1.5;

  let level: RiskLevel;
  if (score === 0) level = 'low';
  else if (score <= 2) level = 'int-1';
  else if (score <= 4.5) level = 'int-2';
  else level = 'high';

  return { score, level };
};

// ── CPSS-Mol (2016) ──

export const cpssMolRiskGroups: RiskGroup[] = [
  { level: 'low', label: 'Low', tone: 'green', criteria: '0 points (median survival: N/R)' },
  { level: 'int-1', label: 'Intermediate-1', tone: 'green', criteria: '1 point (median survival: 64 mo)' },
  { level: 'int-2', label: 'Intermediate-2', tone: 'yellow', criteria: '2 points (median survival: 37 mo)' },
  { level: 'high', label: 'High', tone: 'red', criteria: '≥3 points (median survival: 18 mo)' },
];

/**
 * CPSS-Mol (2016):
 *  WBC count ≥13,000 → 1 pt
 *  BM blasts ≥5% → 1 pt
 *  RUNX1 mutation → 2 pts
 *  ASXL1 mutation → 1 pt
 *  NRAS mutation → 1 pt
 *  SETBP1 mutation → 1 pt
 *  CPSS cytogenetic intermediate → 1 pt
 *  CPSS cytogenetic high → 2 pts (mutually exclusive with intermediate)
 *  RBC transfusion-dependence (≥1 per 8 wk for 4 mo) → 1 pt
 */
export const calculateCpssMol = (
  wbc: number,
  bmBlasts: number,
  runx1: boolean,
  asxl1: boolean,
  nras: boolean,
  setbp1: boolean,
  cpssKaryotype: 'normal' | 'intermediate' | 'high',
  transfusionDependent: boolean
): { score: number; level: RiskLevel } => {
  let score = 0;
  if (wbc >= 13) score += 1;
  if (bmBlasts >= 5) score += 1;
  if (runx1) score += 2;
  if (asxl1) score += 1;
  if (nras) score += 1;
  if (setbp1) score += 1;
  if (cpssKaryotype === 'high') score += 2;
  else if (cpssKaryotype === 'intermediate') score += 1;
  if (transfusionDependent) score += 1;

  let level: RiskLevel;
  if (score === 0) level = 'low';
  else if (score === 1) level = 'int-1';
  else if (score === 2) level = 'int-2';
  else level = 'high';

  return { score, level };
};

// ── BLAST (2025) ──

export const blastRiskGroups: RiskGroup[] = [
  { level: 'low', label: 'Low', tone: 'green', criteria: '0 points (median survival: 63 mo)' },
  { level: 'intermediate', label: 'Intermediate', tone: 'yellow', criteria: '1 point (median survival: 28 mo)' },
  { level: 'high', label: 'High', tone: 'red', criteria: '2–4 points (median survival: 13 mo)' },
];

/**
 * BLAST (2025):
 *  Circulating blasts ≥2% → 1 pt
 *  Leukocyte count ≥13 × 10⁹/L → 1 pt
 *  Anemia (sex-dependent):
 *    Severe: Hb <8 (F) or <9 (M), or need for red cell transfusions → 2 pts
 *    Moderate: Hb 8–<10 (F) or 9–<11 (M) → 1 pt
 */
const blastClinicalPoints = (
  sex: 'male' | 'female',
  hb: number,
  blasts: number,
  wbc: number,
  transfusionNeed: boolean
): number => {
  let score = 0;
  if (blasts >= 2) score += 1;
  if (wbc >= 13) score += 1;

  const severeHb = sex === 'female' ? 8 : 9;
  const moderateHb = sex === 'female' ? 10 : 11;
  if (transfusionNeed || hb < severeHb) score += 2;
  else if (hb < moderateHb) score += 1;

  return score;
};

export const calculateBlast = (
  sex: 'male' | 'female',
  hb: number,
  blasts: number,
  wbc: number,
  transfusionNeed: boolean
): { score: number; level: RiskLevel } => {
  const score = blastClinicalPoints(sex, hb, blasts, wbc, transfusionNeed);

  let level: RiskLevel;
  if (score === 0) level = 'low';
  else if (score === 1) level = 'intermediate';
  else level = 'high';

  return { score, level };
};

// ── BLAST-mol (2025) ──

export const blastMolRiskGroups: RiskGroup[] = [
  { level: 'low', label: 'Low', tone: 'green', criteria: '0–1 points (median survival: 74 mo)' },
  { level: 'intermediate', label: 'Intermediate', tone: 'yellow', criteria: '2–3 points (median survival: 23 mo)' },
  { level: 'high', label: 'High', tone: 'red', criteria: '4–6 points (median survival: 12 mo)' },
];

/**
 * BLAST-mol (2025):
 *  Same 3 clinical variables as BLAST (blasts, WBC, anemia)
 *  + Molecular risk score: high=2, intermediate=1, low=0
 */
export const calculateBlastMol = (
  sex: 'male' | 'female',
  hb: number,
  blasts: number,
  wbc: number,
  transfusionNeed: boolean,
  molecularRisk: 'low' | 'intermediate' | 'high'
): { score: number; level: RiskLevel } => {
  // Start with the clinical portion (same as BLAST)
  let score = blastClinicalPoints(sex, hb, blasts, wbc, transfusionNeed);

  // Molecular risk score
  if (molecularRisk === 'high') score += 2;
  else if (molecularRisk === 'intermediate') score += 1;

  let level: RiskLevel;
  if (score <= 1) level = 'low';
  else if (score <= 3) level = 'intermediate';
  else level = 'high';

  return { score, level };
};
